import type { ApolloCache, ObservableQuery, Reference } from '@apollo/client';
import { datadogRum } from '@datadog/browser-rum';
import {
  App,
  ChatRoomKind,
  GetCoachChatRoomQuery,
  GetCoachChatTotalUnreadQuery,
  NewMessagesDocument,
  NewMessagesSubscription,
  NewMessagesSubscriptionVariables,
  NewTotalUnreadMessageCountDocument,
  NewTotalUnreadMessageCountSubscription,
  NewTotalUnreadMessageCountSubscriptionVariables,
} from 'graphql/types';
import uniqBy from 'lodash/uniqBy';

/**
 * Merges an incoming newMessages subscription event into the cached
 * GetCoachChatRoom query result.
 *
 * Handles three cases:
 * - Duplicate by real id (server echo of an optimistic message)
 * - Duplicate by optimistic id placeholder (replace optimistic node)
 * - Genuinely new message (append)
 */
export const mergeNewMessageIntoCoachChatRoom = (
  prev: GetCoachChatRoomQuery,
  subscriptionData: NewMessagesSubscription,
): GetCoachChatRoomQuery => {
  const payload = subscriptionData.newMessages;
  if (!payload || !prev.self?.chatRooms?.nodes) {
    return prev;
  }

  const { message: newMessage, chatRoom: targetRoom, optimisticId } = payload;

  let roomFound = false;
  const updatedNodes = prev.self.chatRooms.nodes.map((room) => {
    if (room.id !== targetRoom.id) {
      return room;
    }
    roomFound = true;

    const existing = room.messages.nodes ?? [];
    let replaced = false;

    const merged = existing.map((msg) => {
      const isRealIdMatch = msg.id === newMessage.id;
      const isOptimisticMatch =
        optimisticId != null && msg.id === `optimistic/${optimisticId}`;

      if (isRealIdMatch || isOptimisticMatch) {
        replaced = true;
        return newMessage;
      }

      return msg;
    });

    if (!replaced) {
      merged.push(newMessage);
    }

    const previousUnreadCount = room.messages.unreadMessageCount ?? 0;
    const selfId = prev.self?.id;
    const senderId = newMessage.user?.id;
    const isFromOtherUser =
      selfId != null && senderId != null && senderId !== selfId;
    const lastReadAt = room.lastMessageRead;
    const messageCreatedAt = new Date(newMessage.createdAt as string).getTime();
    const hasBeenReadAlready =
      lastReadAt != null &&
      new Date(lastReadAt as string).getTime() >= messageCreatedAt;
    const shouldIncrementUnread =
      !replaced && isFromOtherUser && !hasBeenReadAlready;

    return {
      ...room,
      lastMessageReceivedAt: newMessage.createdAt,
      messages: {
        ...room.messages,
        nodes: uniqBy(merged, 'id'),
        unreadMessageCount: shouldIncrementUnread
          ? previousUnreadCount + 1
          : previousUnreadCount,
      },
    };
  });

  if (!roomFound) {
    return prev;
  }

  return {
    ...prev,
    self: {
      ...prev.self,
      chatRooms: {
        ...prev.self.chatRooms,
        nodes: updatedNodes,
      },
    },
  };
};

/**
 * Appends a sent message to a specific ChatRoom's messages in the Apollo cache
 * using cache.modify, following the same pattern as heart-mobile.
 *
 * This targets the ChatRoom entity directly by ID, avoiding the need to
 * read/write the entire GetCoachChatRoom query.
 */
export const mergeSentMessageIntoCache = (
  cache: ApolloCache,
  chatId: string,
  sentMessage: { id: string; createdAt: unknown },
): void => {
  cache.modify({
    id: cache.identify({ __typename: 'ChatRoom', id: chatId }),
    fields: {
      messages(existingValue, { toReference }) {
        const existing =
          existingValue && typeof existingValue === 'object'
            ? (existingValue as {
                nodes: Reference[];
                unreadMessageCount: number;
              })
            : {
                nodes: [] as Reference[],
                unreadMessageCount: 0,
              };
        const newRef = toReference({
          __typename: 'Message',
          id: sentMessage.id,
        });

        if (!newRef) {
          return existing;
        }

        if (existing.nodes?.some((ref) => ref.__ref === newRef.__ref)) {
          return existing;
        }

        return {
          ...existing,
          nodes: [...(existing.nodes ?? []), newRef],
        };
      },
      lastMessageReceivedAt() {
        return sentMessage.createdAt;
      },
    },
  });
};

/**
 * Calls subscribeToMore on the GetCoachChatRoom query to receive real-time
 * messages. Returns the unsubscribe function.
 *
 * When chatId is provided, subscribes only to that chat (matches coach-hub
 * behavior and reduces backend load). When chatId is undefined, subscribes
 * to all new messages for the user (e.g. when on the chat list view).
 */
export const coachChatSubscribeToMore = (
  subscribeToMore: ObservableQuery<GetCoachChatRoomQuery>['subscribeToMore'],
  chatId?: string | null,
): (() => void) =>
  subscribeToMore<NewMessagesSubscription, NewMessagesSubscriptionVariables>({
    document: NewMessagesDocument,
    variables: chatId != null ? { chatId } : undefined,
    updateQuery: (unsafePrev, { subscriptionData }) => {
      // prev comes from the cache and is complete at runtime; v4 types it as
      // DeepPartial.
      const prev = unsafePrev as GetCoachChatRoomQuery;
      if (!subscriptionData.data) {
        return prev;
      }
      return mergeNewMessageIntoCoachChatRoom(prev, subscriptionData.data);
    },
    onError: (err) => {
      datadogRum.addError(err);
    },
  });

/**
 * Merges a new total-unread count from the subscription into the
 * GetCoachChatTotalUnread query cache. Used by coachChatTotalUnreadSubscribeToMore.
 */
export const mergeTotalUnreadIntoCoachChatTotalUnread = (
  prev: GetCoachChatTotalUnreadQuery,
  newCount: number,
): GetCoachChatTotalUnreadQuery => {
  if (!prev.self) {
    return prev;
  }
  return {
    ...prev,
    self: {
      ...prev.self,
      coachData: {
        ...prev.self.coachData,
        totalUnreadMessageCount: newCount,
      },
    },
  };
};

/**
 * Subscribes to newTotalUnreadMessageCount and merges the count into the
 * GetCoachChatTotalUnread query cache (aligns with coach-hub badge behavior).
 * Uses same kinds/apps as coach-hub: Direct + Heart.
 */
export const coachChatTotalUnreadSubscribeToMore = (
  subscribeToMore: ObservableQuery<GetCoachChatTotalUnreadQuery>['subscribeToMore'],
): (() => void) =>
  subscribeToMore<
    NewTotalUnreadMessageCountSubscription,
    NewTotalUnreadMessageCountSubscriptionVariables
  >({
    document: NewTotalUnreadMessageCountDocument,
    variables: {
      kinds: [ChatRoomKind.Direct],
      apps: [App.Heart],
    },
    updateQuery: (unsafePrev, { subscriptionData }) => {
      // prev comes from the cache and is complete at runtime; v4 types it as
      // DeepPartial.
      const prev = unsafePrev as GetCoachChatTotalUnreadQuery;
      if (!subscriptionData.data) {
        return prev;
      }
      return mergeTotalUnreadIntoCoachChatTotalUnread(
        prev,
        subscriptionData.data.newTotalUnreadMessageCount,
      );
    },
    onError: (err) => {
      datadogRum.addError(err);
    },
  });
